import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@repo/utils";

type TooltipPlacement = "top" | "bottom" | "left" | "right";

type Props = {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  delay?: number;
  placement?: TooltipPlacement;
};

const GAP = 6;
const EDGE = 4;

export const HoverTooltip = ({
  content,
  children,
  className,
  wrapperClassName,
  delay = 400,
  placement = "top",
}: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
    setCoords(null);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const tip = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = trigger.top - tip.height - GAP;
        left = trigger.left + (trigger.width - tip.width) / 2;
        if (top < EDGE) {
          top = trigger.bottom + GAP;
        }
        break;

      case "bottom":
        top = trigger.bottom + GAP;
        left = trigger.left + (trigger.width - tip.width) / 2;
        if (top + tip.height > window.innerHeight - EDGE) {
          top = trigger.top - tip.height - GAP;
        }
        break;

      case "left":
        top = trigger.top + (trigger.height - tip.height) / 2;
        left = trigger.left - tip.width - GAP;
        if (left < EDGE) {
          left = trigger.right + GAP;
        }
        break;

      case "right":
        top = trigger.top + (trigger.height - tip.height) / 2;
        left = trigger.right + GAP;
        if (left + tip.width > window.innerWidth - EDGE) {
          left = trigger.left - tip.width - GAP;
        }
        break;
    }

    left = Math.max(EDGE, Math.min(left, window.innerWidth - tip.width - EDGE));
    top = Math.max(EDGE, Math.min(top, window.innerHeight - tip.height - EDGE));

    setCoords({ top, left });
  }, [isVisible, placement, content]);

  useEffect(() => {
    if (!isVisible) return;

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [isVisible, hide]);

  return (
    <span
      ref={triggerRef}
      className={cn("inline-flex", wrapperClassName)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {isVisible &&
        content &&
        createPortal(
          <span
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: "fixed",
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              visibility: coords ? "visible" : "hidden",
            }}
            className={cn(
              "pointer-events-none z-[9999] rounded bg-gray-900 px-2 py-1 text-xs whitespace-nowrap text-white shadow-lg",
              className
            )}
          >
            {content}
          </span>,
          document.body
        )}
    </span>
  );
};
